import React from 'react';

/**
 * Accessible settings switch.
 *
 * Uses an explicit `role="switch"` button so screen readers announce the
 * control and its state. The current state is also written as visible
 * "On"/"Off" text so the value never depends on color alone. The button
 * is a 44px touch target that wraps a compact pill track, so the control
 * stays comfortable to use without rendering as a large circle.
 */
function SettingsSwitch({
  id,
  label,
  description,
  checked,
  onChange,
  disabled = false,
  onLabel = 'On',
  offLabel = 'Off',
}) {
  const labelId = `${id}-label`;
  const descId = description ? `${id}-desc` : undefined;

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0 flex-1">
        <p id={labelId} className="text-sm font-semibold text-gray-900 dark:text-white">
          {label}
        </p>
        {description ? (
          <p id={descId} className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
            {description}
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2.5 shrink-0">
        <span
          className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400 w-6 text-right"
          aria-hidden="true"
        >
          {checked ? onLabel : offLabel}
        </span>

        <button
          type="button"
          role="switch"
          id={id}
          aria-checked={Boolean(checked)}
          aria-labelledby={labelId}
          aria-describedby={descId}
          onClick={() => onChange(!checked)}
          disabled={disabled}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span
            aria-hidden="true"
            className={`inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              checked
                ? 'bg-green-600 dark:bg-green-500'
                : 'bg-gray-300 dark:bg-neutral-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                checked ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </span>
        </button>
      </div>
    </div>
  );
}

export default SettingsSwitch;
